// Small offline write queue.
//
// Scope is deliberately narrow: only likes, saves, follows, and comments
// get queued (see the call sites in app.js). Those are all low-stakes,
// effectively toggle/append actions where "do it again once you're back
// online" is a safe thing to promise. Chat, location-sharing, payments,
// media uploads, and profile/place edits are NOT queued here — those need
// either a live connection or real conflict handling this module doesn't
// attempt, so they're left to fail normally offline.
import { apiPost, apiPatch, apiDelete } from './api.js';

const QUEUE_KEY = 'thikana_offline_queue';

// fetch() itself throws a plain TypeError when the request never reached
// the network at all (offline, DNS failure, dropped connection mid-flight).
// A response that DID reach the server — even a rejection — comes back as
// our own apiError() Error with a .status set, which is a real answer, not
// a connectivity problem, and should never be queued.
export function isNetworkError(e) {
  return e instanceof TypeError;
}

function loadQueue() {
  try { return JSON.parse(localStorage.getItem(QUEUE_KEY)) || []; }
  catch (e) { return []; }
}
function saveQueue(q) {
  try { localStorage.setItem(QUEUE_KEY, JSON.stringify(q)); }
  catch (e) { /* quota/private-mode — the action just won't survive a reload */ }
}

export function queueLength() { return loadQueue().length; }

export function queueOfflineAction(method, path, body, label) {
  const q = loadQueue();
  q.push({
    id: Date.now() + '-' + Math.random().toString(36).slice(2),
    method, path, body: body || null, label: label || '',
    ts: Date.now()
  });
  saveQueue(q);
}

// Replays every queued action in the order it was originally made (so two
// queued taps on the same like button, say, still land in the right final
// state). Anything that fails again with a network error goes back on the
// queue for the next attempt; anything that fails for a real reason (the
// server rejects it — e.g. the post was deleted meanwhile) is dropped
// rather than retried forever.
export async function flushOfflineQueue() {
  const q = loadQueue();
  if (!q.length) return { synced: 0, failed: 0 };
  const remaining = [];
  let synced = 0, failed = 0;
  for (const item of q) {
    try {
      if (item.method === 'POST') await apiPost(item.path, item.body);
      else if (item.method === 'PATCH') await apiPatch(item.path, item.body);
      else if (item.method === 'DELETE') await apiDelete(item.path);
      synced++;
    } catch (e) {
      if (isNetworkError(e)) remaining.push(item);
      else failed++;
    }
  }
  saveQueue(remaining);
  return { synced, failed };
}
